import React, { useCallback, useContext, useMemo, useState } from 'react';

import { Form } from 'react-final-form';
import { NotificationManager } from 'react-notifications';
import { useLocation } from 'react-router-dom';

import { FORM_ERROR } from 'final-form';

/** components */
import PhoneVerification from '../components/Auth/PhoneVerification';
import BnplFormContainer from '../components/Bnpl/BnplFormContainer';
import { FormInput } from '../components/FormElements';
import Layout from '../components/common/Layout';
import configData from '../config';

/** context */
import { UserContext } from '../context/UserContext';

/** helpers */
import { formatPhoneNumber } from '../helpers/format';
import { composeValidators, email, phone as phoneValidator, required } from '../helpers/validation';
import { getProfile } from '../service/api';

/** service */
import {
  changeEmail,
  changePhone,
  phoneVerificationManageProfile,
  phoneVerificationManageProfileResendCode,
} from '../service/profile';

const Profile = () => {
  const location = useLocation();
  const [phone, setPhone] = useState();
  const [intentId, setIntentId] = useState();
  const { profile, setProfile } = useContext(UserContext);
  const query = useMemo(() => new URLSearchParams(location.search), [location]);
  const destination = query.get('app');
  const isBnpl = configData.getTenant() === 'lightrx';

  const formOptions = useMemo(() => {
    return {
      namesFields: [
        {
          label: 'First name',
          placeholder: 'Enter your first name',
          required: true,
          validate: required("First name can't be empty"),
          name: 'firstName',
          disabled: true,
        },
        {
          label: 'Last name',
          placeholder: 'Enter your last name',
          name: 'lastName',
          validate: required("Last name can't be empty"),
          required: true,
          disabled: true,
        },
      ],
      emailFields: [
        {
          label: 'Email Address',
          placeholder: 'Enter your email address',
          required: true,
          type: 'email',
          validate: composeValidators(required("Email can't be empty"), email('Email is invalid')),
          name: 'email',
        },
      ],
      phoneFields: [
        {
          label: 'Phone Number',
          required: true,
          type: 'tel',
          name: 'phone',
          validate: composeValidators(
            required("Phone can't be empty"),
            phoneValidator('Phone is invalid')
          ),
        },
      ],
    };
  }, []);

  const onSubmitInternal = useCallback(
    async (values) => {
      try {
        if (values.email !== profile.email) {
          const response = await changeEmail({ email: values.email });
          const { phone, intentId } = response.data;
          setPhone(phone);
          setIntentId(intentId);
          return;
        }

        if (values.phone !== profile.phone) {
          const response = await changePhone({ phone: values.phone.replaceAll('-', '') });
          const { phone, intentId } = response.data;
          setPhone(phone);
          setIntentId(intentId);
        }
      } catch (err) {
        let message = 'Save failed. Please verify all the fields and try again';
        // TODO: If I know errors code I'll add proper messaging
        // const duplicated = JSON.parse(err.request.response)?.errors.find((e) => e.code === 'DUPLICATED_ENTITY');
        // if (duplicated) {
        //   switch (duplicated?.info?.entity) {
        //     case 'EMAIL':
        //       message = 'Email already registered';
        //       break;
        //     case 'PHONE':
        //       message = 'Phone already registered';
        //       break;
        //   }
        // }

        NotificationManager.warning(message, 'Warning');
        return { [FORM_ERROR]: message };
      }
    },
    [profile]
  );

  const onPhoneVerificationSubmit = useCallback(
    async ({ code }) => {
      try {
        await phoneVerificationManageProfile({ intentId, code });
        const profile = await getProfile();
        setProfile(profile.data);

        NotificationManager.success('Profile details have been updated!', 'Success');
        setIntentId(undefined);
        setPhone(undefined);
      } catch (err) {
        const error = new Error('An error occurred the profile info could not be updated');
        error.name = 'Pofile_Err';
        throw error;
      }
    },
    [intentId, setProfile]
  );

  const onPhoneVerificationResendCode = useCallback(async () => {
    await phoneVerificationManageProfileResendCode({ intent: intentId });
  }, [intentId]);

  const renderContent = () => (
    <div className="profile section">
      {intentId && phone ? (
        <PhoneVerification
          onSubmit={onPhoneVerificationSubmit}
          phone={phone}
          onResendCode={onPhoneVerificationResendCode}
          title="Enter confirmation code to save changes"
          className={destination === '/admin' ? 'admin' : 'auth'}
        />
      ) : (
        <>
          <div className="auth-header">
            <h1>Manage Profile</h1>
          </div>
          <div className="auth-main profile-main">
            {['namesFields', 'emailFields', 'phoneFields'].map((formType) => {
              return (
                <Form
                  onSubmit={onSubmitInternal}
                  key={`profile${formType}`}
                  initialValues={{
                    ...profile,
                    phone: formatPhoneNumber(profile.phone),
                  }}
                  render={({ handleSubmit, form, submitting, pristine, submitError }) => (
                    <form
                      onSubmit={handleSubmit}
                      className={`form ${formType !== 'namesFields' ? 'd-flex' : ''}`}
                    >
                      {formOptions[formType].map((f) => (
                        <FormInput {...f} key={`profile_${f.name}`} />
                      ))}
                      {formType !== 'namesFields' && (
                        <div className="actions">
                          <button
                            className="btn primary-btn"
                            type="submit"
                            disabled={submitting || pristine}
                          >
                            Save changes
                          </button>
                        </div>
                      )}
                    </form>
                  )}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );

  if (isBnpl) {
    return <BnplFormContainer isDesktop={true}>{renderContent()}</BnplFormContainer>;
  }

  return <Layout>{renderContent()}</Layout>;
};

export default Profile;
